import { useState } from 'react'
import { createPortal } from 'react-dom'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery } from '@tanstack/react-query'

import { globalBootstrap } from '../..'
import { initializeChat } from '../../services/whatsapp'
import { getMetaTemplates } from '../../services/NewServicesCompnay'
import { searchTalents } from '../../services/searchTalents'
import { TAGS } from '../../services/Tags'

import Modal from '../Modal'
import Loader from '../Loader'
import Toast from '../Toast/Toast'

const WhatsappIcon = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M13.601 2.326A7.854 7.854 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.933 7.933 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.898 7.898 0 0 0 13.6 2.326zM7.994 14.521a6.573 6.573 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.557 6.557 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592zm3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.066-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.729.729 0 0 0-.529.247c-.182.198-.691.677-.691 1.654 0 .977.71 1.916.81 2.049.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232z" />
  </svg>
)

/**
 * Boton reutilizable que abre un modal con un formulario para inicializar
 * el chat de WhatsApp de un talento usando el servicio `initializeChat`.
 *
 * - Si recibe `talentId`, el formulario solo pide la plantilla (super buscador y detalle).
 * - Si NO recibe `talentId`, el formulario permite buscar el talento por nombre /
 *   username (servicio generico `searchTalents`) y resuelve su ID (modulo de chat).
 */
const InitWhatsappChatButton = ({
  modalId,
  talentId = null,
  talentName = '',
  triggerClassName = 'btn btn-sm btn-success d-flex align-items-center gap-2',
  triggerLabel = 'WhatsApp',
  showLabel = true,
  onTriggerClick,
}) => {
  const hasTalentId = talentId !== null && talentId !== undefined && talentId !== ''

  const [search, setSearch] = useState('')
  const [showDropdown, setShowDropdown] = useState(false)
  const [selectedTalent, setSelectedTalent] = useState(null)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: { templeteName: '' },
  })

  const { data: talentsData, isFetching } = useQuery(
    [TAGS.search_talents, 'init-ws-chat', search],
    () => searchTalents({ page: 0, search: search.trim() }),
    { enabled: !hasTalentId && showDropdown && search.trim().length > 0 }
  )

  const talents = talentsData?.listTalents ?? []

  const { data: metaTemplatesData, isFetching: isFetchingTemplates } = useQuery(
    [TAGS.whatsapp_templates],
    getMetaTemplates
  )

  const templates = Array.isArray(metaTemplatesData?.res?.result)
    ? metaTemplatesData.res.result
    : []

  const resetTalentSearch = () => {
    setSearch('')
    setShowDropdown(false)
    setSelectedTalent(null)
  }

  const { mutate, isLoading } = useMutation(
    ['whatsapp-initialize-chat'],
    initializeChat,
    {
      onSuccess: ({ response } = {}) => {
        if (!response || !response.ok) {
          Toast({
            title: 'No se pudo iniciar el chat de WhatsApp',
            icon: 'error',
          })
          return
        }
        Toast({ title: 'Chat de WhatsApp iniciado correctamente' })
        reset()
        resetTalentSearch()
        globalBootstrap.Modal.getOrCreateInstance(`#${modalId}`).hide()
      },
      onError: () => {
        Toast({
          title: 'No se pudo iniciar el chat de WhatsApp',
          icon: 'error',
        })
      },
    }
  )

  const handleSelectTalent = (talent) => {
    setSelectedTalent(talent)
    setShowDropdown(false)
  }

  const handleOpenModal = (e) => {
    // Evita que el click seleccione la card que envuelve al boton, pero abre
    // el modal de forma programatica (no dependemos del burbujeo del data-api).
    e?.stopPropagation?.()
    onTriggerClick?.(e)
    globalBootstrap.Modal.getOrCreateInstance(`#${modalId}`).show()
  }

  const onSubmit = (data) => {
    const resolvedTalentId = hasTalentId ? talentId : selectedTalent?.id

    if (!resolvedTalentId) {
      Toast({ title: 'Selecciona un talento', icon: 'error' })
      return
    }

    mutate({
      talentId: Number(resolvedTalentId),
      templeteName: data.templeteName,
    })
  }

  return (
    <>
      <button
        type="button"
        className={triggerClassName}
        title="Iniciar chat de WhatsApp"
        onClick={handleOpenModal}
      >
        <WhatsappIcon />
        {showLabel && <span>{triggerLabel}</span>}
      </button>

      {createPortal(
        <Modal modalId={modalId} title="Iniciar chat de WhatsApp">
        <form onSubmit={handleSubmit(onSubmit)} className="d-flex flex-column gap-3">
          {hasTalentId ? (
            <div>
              <label className="form-label text-on-bg-primary">Talento</label>
              <input
                type="text"
                className="form-control"
                value={talentName || `ID ${talentId}`}
                disabled
                readOnly
              />
            </div>
          ) : (
            <div className="position-relative">
              <label className="form-label text-on-bg-primary">Talento</label>
              <input
                type="text"
                className="form-control"
                autoComplete="off"
                placeholder="Buscar talento por nombre o username…"
                value={
                  selectedTalent
                    ? `${selectedTalent?.user?.names ?? ''} ${
                        selectedTalent?.user?.surnames ?? ''
                      } — @${selectedTalent?.user?.username ?? ''}`.trim()
                    : search
                }
                onFocus={() => setShowDropdown(true)}
                onChange={(e) => {
                  setSearch(e.target.value)
                  setSelectedTalent(null)
                  setShowDropdown(true)
                }}
              />

              {showDropdown && search.trim().length > 0 && (
                <div
                  className="position-absolute w-100 bg-white rounded shadow-sm"
                  style={{
                    zIndex: 1050,
                    border: '1px solid #e5e7eb',
                    maxHeight: '220px',
                    overflowY: 'auto',
                  }}
                >
                  {isFetching && (
                    <div className="d-flex align-items-center gap-2 px-3 py-3">
                      <div
                        className="spinner-border spinner-border-sm text-secondary"
                        role="status"
                      />
                      <span style={{ fontSize: '13px', color: '#6b7280' }}>
                        Buscando…
                      </span>
                    </div>
                  )}
                  {!isFetching && talents.length === 0 && (
                    <p
                      className="px-3 py-3 mb-0"
                      style={{ fontSize: '13px', color: '#6b7280' }}
                    >
                      No se encontraron talentos.
                    </p>
                  )}
                  {!isFetching &&
                    talents.map((talent) => {
                      const fullName = `${talent?.user?.names ?? ''} ${
                        talent?.user?.surnames ?? ''
                      }`.trim()
                      const username = talent?.user?.username ?? ''
                      const email = talent?.user?.email ?? ''
                      return (
                        <div
                          key={talent.id}
                          className="px-3 py-2 cursor-pointer"
                          style={{ borderBottom: '1px solid #f3f4f6' }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#f9fafb'
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#ffffff'
                          }}
                          onClick={() => handleSelectTalent(talent)}
                        >
                          <p
                            className="mb-0 fw-bold"
                            style={{ fontSize: '13px', color: '#111827' }}
                          >
                            {fullName || username}
                          </p>
                          <p
                            className="mb-0"
                            style={{ fontSize: '11px', color: '#6b7280' }}
                          >
                            @{username}
                            {email ? ` · ${email}` : ''}
                          </p>
                        </div>
                      )
                    })}
                </div>
              )}
            </div>
          )}

          <div>
            <label className="form-label text-on-bg-primary">Plantilla</label>
            <select
              className="form-select"
              disabled={isFetchingTemplates}
              {...register('templeteName', {
                required: 'Selecciona una plantilla',
              })}
            >
              <option value="">
                {isFetchingTemplates ? 'Cargando plantillas…' : 'Selecciona una plantilla'}
              </option>
              {templates.map((templateName) => (
                <option key={templateName} value={templateName}>
                  {templateName}
                </option>
              ))}
            </select>
            {errors.templeteName && (
              <span className="text-danger font-small-2">
                {errors.templeteName.message}
              </span>
            )}
          </div>

          <div className="d-flex justify-content-end gap-2 mt-2">
            <button
              type="button"
              className="btn btn-white border border-on-bg-primary text-on-bg-primary"
              data-bs-dismiss="modal"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-success d-flex align-items-center gap-2"
              disabled={isLoading}
            >
              {isLoading ? <Loader /> : <WhatsappIcon />}
              <span>Iniciar chat</span>
            </button>
          </div>
        </form>
        </Modal>,
        document.body
      )}
    </>
  )
}

export default InitWhatsappChatButton
